import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, Loader2 } from "lucide-react";
import * as React from "react";
import { Logo } from "@/components/brand/logo";
import { useDashboardContext } from "@/components/dashboard/DashboardContext";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useSubscription } from "@/hooks/use-subscription";

async function bootstrapWorkspace() {
	const res = await fetch("/api/workspaces/bootstrap", { method: "POST" });
	if (!res.ok) {
		const data = await res.json().catch(() => null);
		throw new Error(
			typeof data?.error === "string"
				? data.error
				: "Failed to create workspace",
		);
	}
	return res.json() as Promise<{ workspace?: { id?: string } }>;
}

export function WelcomeDialog() {
	const navigate = useNavigate();
	const { workspaces, isWorkspacesLoading } = useDashboardContext();
	const { isActive, isLoading } = useSubscription();
	const [isPending, setIsPending] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);
	const [dismissedAfterBootstrap, setDismissedAfterBootstrap] =
		React.useState(false);

	const isOpen =
		!dismissedAfterBootstrap && !isWorkspacesLoading && workspaces.length === 0;
	const needsCheckout = !isActive;
	const canConnectAccounts = isActive && !needsCheckout;

	React.useEffect(() => {
		if (!isOpen || isLoading || canConnectAccounts) return;
		void navigate({
			to: "/choose-plan",
			search: { redirect: undefined, reason: "subscription_required" },
			replace: true,
		});
	}, [canConnectAccounts, isLoading, isOpen, navigate]);

	const handleGetStarted = async () => {
		if (isPending) return;
		setIsPending(true);
		setError(null);
		try {
			await bootstrapWorkspace();
			setDismissedAfterBootstrap(true);
			await navigate({
				to: "/dashboard/connect",
				search: { onboarding: "connect" },
			});
		} catch (err) {
			setError(err instanceof Error ? err.message : "Something went wrong");
			setIsPending(false);
		}
	};

	return (
		<Dialog open={isOpen && !isLoading && canConnectAccounts}>
			<DialogContent
				onInteractOutside={(event) => event.preventDefault()}
				onEscapeKeyDown={(event) => event.preventDefault()}
				showCloseButton={false}
				className="gap-4 p-5 sm:max-w-md"
				overlayClassName="dark:bg-black/85 dark:backdrop-blur-sm"
			>
				<DialogHeader className="items-center gap-3 text-center">
					<div className="relative flex items-center justify-center">
						<div
							aria-hidden
							className="absolute inset-0 -z-10 rounded-full bg-primary/30 blur-2xl"
						/>
						<Logo size="sm" disableLink showText={false} />
					</div>
					<div className="space-y-1">
						<DialogTitle className="text-lg">
							Connect your first account
						</DialogTitle>
						<DialogDescription className="mx-auto max-w-md text-sm leading-6">
							Link one social account so AgentReacher can create and schedule
							posts for you.
						</DialogDescription>
					</div>
				</DialogHeader>

				{error && <p className="text-center text-xs text-red-500">{error}</p>}

				<div className="flex items-center justify-center">
					<button
						type="button"
						onClick={handleGetStarted}
						disabled={isPending || isLoading}
						className="inline-flex h-9 min-w-40 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
					>
						{isPending || isLoading ? (
							<>
								<Loader2 className="size-4 animate-spin" />
								Getting ready...
							</>
						) : (
							<>
								Connect accounts
								<ArrowRight className="size-4" />
							</>
						)}
					</button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
